import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import NHLGameCard from './NHLGameCard';
const NHLConfidenceTiers = ({ games }) => {
    // Helper function to calculate average metric for a tier
    const calculateTierAverage = (tierGames, metricGetter, fallback) => {
        if (tierGames.length === 0)
            return fallback;
        const sum = tierGames.reduce((acc, game) => acc + metricGetter(game), 0);
        return sum / tierGames.length;
    };
    // Helper function to get bet grade from game
    const getBetGrade = (game) => {
        // Prefer direct bet_grade from JSON (CBB, NBA) or betting_indicators (NHL)
        if (game.bet_grade)
            return game.bet_grade.toUpperCase();
        const actualGrade = game.betting_indicators?.bet_ranking?.grade;
        if (actualGrade)
            return actualGrade.toUpperCase();
        // Fallback calculation based on confidence and edge
        const confidence = (game.ensemble_probability || 0) * 100;
        const edge = (game.betting_edge || 0) * 100;
        const confidenceLevel = game.confidence_level || 'LOW';
        if (confidenceLevel === 'HIGH' && confidence >= 70 && edge >= 15)
            return 'A+';
        if (confidenceLevel === 'HIGH' && confidence >= 68)
            return 'A';
        if (confidenceLevel === 'HIGH')
            return 'A-';
        if (confidenceLevel === 'MEDIUM_PLUS' && confidence >= 65)
            return 'B+';
        if (confidenceLevel === 'MEDIUM_PLUS')
            return 'B';
        if (confidenceLevel === 'MEDIUM' && confidence >= 60)
            return 'B-';
        if (confidenceLevel === 'MEDIUM')
            return 'C+';
        if (confidenceLevel === 'LOW')
            return 'C';
        return 'C-';
    };
    // Remove duplicates by creating a Set of unique game IDs we've seen
    const processedGameIds = new Set();
    // Filter games by bet grade (A, B, C, D)
    const aGradeGames = games.filter(g => {
        if (processedGameIds.has(g.game_id || g.id))
            return false;
        const grade = getBetGrade(g);
        if (grade.startsWith('A')) {
            processedGameIds.add(g.game_id || g.id);
            return true;
        }
        return false;
    });
    const bGradeGames = games.filter(g => {
        if (processedGameIds.has(g.game_id || g.id))
            return false;
        const grade = getBetGrade(g);
        if (grade.startsWith('B')) {
            processedGameIds.add(g.game_id || g.id);
            return true;
        }
        return false;
    });
    const cGradeGames = games.filter(g => {
        if (processedGameIds.has(g.game_id || g.id))
            return false;
        const grade = getBetGrade(g);
        if (grade.startsWith('C')) {
            processedGameIds.add(g.game_id || g.id);
            return true;
        }
        return false;
    });
    const dGradeGames = games.filter(g => {
        if (processedGameIds.has(g.game_id || g.id))
            return false;
        const grade = getBetGrade(g);
        if (grade.startsWith('D') || grade.startsWith('F') || !grade) {
            processedGameIds.add(g.game_id || g.id);
            return true;
        }
        return false;
    });
    const tiers = [
        {
            level: 'A_GRADE',
            label: 'A Grade Bets (Premium)',
            color: '#198754', // Green for A grade
            games: aGradeGames,
            historical_accuracy: calculateTierAverage(aGradeGames, g => 72.0, 72.0),
            historical_roi: calculateTierAverage(aGradeGames, g => 14.0, 14.0)
        },
        {
            level: 'B_GRADE',
            label: 'B Grade Bets (Strong)',
            color: '#0dcaf0', // Cyan for B grade
            games: bGradeGames,
            historical_accuracy: calculateTierAverage(bGradeGames, g => 64.0, 64.0),
            historical_roi: calculateTierAverage(bGradeGames, g => 9.0, 9.0)
        },
        {
            level: 'C_GRADE',
            label: 'C Grade Bets (Moderate)',
            color: '#ffc107', // Yellow for C grade
            games: cGradeGames,
            historical_accuracy: calculateTierAverage(cGradeGames, g => 57.0, 57.0),
            historical_roi: calculateTierAverage(cGradeGames, g => 5.0, 5.0)
        },
        {
            level: 'D_GRADE',
            label: 'D Grade Bets (Avoid)',
            color: '#6c757d', // Gray for D grade
            games: dGradeGames,
            historical_accuracy: calculateTierAverage(dGradeGames, g => 51.0, 51.0),
            historical_roi: calculateTierAverage(dGradeGames, g => 0.0, 0.0)
        }
    ];
    console.log('🏒 NHLConfidenceTiers received games:', games?.length);
    console.log('🏒 Sample game:', games?.[0]);
    if (!games || games.length === 0) {
        return (_jsxs("div", { className: "text-center py-5", children: [_jsx("i", { className: "fas fa-hockey-puck fa-3x text-muted mb-3" }), _jsx("h4", { className: "text-muted", children: "No NHL games available" }), _jsx("p", { className: "text-muted", children: "Check back later for updated predictions." })] }));
    }
    return (_jsx("div", { className: "nhl-confidence-tiers", children: tiers.map((tier) => {
            if (tier.games.length === 0)
                return null;
            return (_jsxs("div", { className: "tier-section mb-5", children: [_jsx("div", { className: "tier-header mb-4", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsxs("h3", { className: "mb-1", style: { color: tier.color, fontSize: '24px', fontWeight: '700' }, children: [_jsx("i", { className: "fas fa-hockey-puck me-2" }), tier.label] }), _jsxs("p", { className: "text-muted mb-0", style: { fontSize: '14px' }, children: [tier.games.length, " game", tier.games.length !== 1 ? 's' : '', " \u2022 Historical: ", tier.historical_accuracy.toFixed(1), "% accuracy, ", tier.historical_roi.toFixed(1), "% ROI"] })] }), _jsx("div", { className: "tier-badge", children: _jsx("span", { className: "badge fs-6 px-3 py-2", style: {
                                            backgroundColor: tier.color,
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: '600'
                                        }, children: tier.games.length }) })] }) }), _jsx("div", { className: "row g-4", children: tier.games
                            .sort((a, b) => (b.ensemble_probability || 0.5) - (a.ensemble_probability || 0.5))
                            .map((game) => (_jsx("div", { className: "col-12 col-md-6 col-xl-4", children: _jsx(NHLGameCard, { game: game, tierColor: tier.color }) }, game.game_id || game.id))) })] }, tier.level));
        }) }));
};
export default NHLConfidenceTiers;
